import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import { useInstallTenantTemplate } from '../../hooks/use-tenant-templates.js';

const s = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  },
  dialog: {
    backgroundColor: tokens.colors.bgCardDark,
    borderRadius: tokens.radius.card,
    padding: '1.5rem',
    width: '500px',
    maxWidth: '90vw',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: tokens.colors.textDark,
    marginBottom: '1.25rem',
  },
  group: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.375rem',
    marginBottom: '1rem',
  },
  label: { fontSize: '0.875rem', fontWeight: 500, color: tokens.colors.textDark },
  input: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    outline: 'none',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: '#e2e8f0',
    padding: '0.5rem 0.75rem',
    fontSize: '0.8125rem',
    fontFamily: 'monospace',
    outline: 'none',
    resize: 'vertical' as const,
    minHeight: '100px',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' },
  btnPrimary: {
    backgroundColor: tokens.colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: tokens.radius.button,
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnCancel: {
    backgroundColor: 'transparent',
    color: tokens.colors.textDark,
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.button,
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
};

interface InstallDialogProps {
  tenantId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function InstallDialog({ tenantId, onClose, onSuccess }: InstallDialogProps) {
  const [code, setCode] = useState('');
  const [version, setVersion] = useState('');
  const [overridesText, setOverridesText] = useState('');

  const install = useInstallTenantTemplate(tenantId);

  function handleInstall() {
    let overrides: Record<string, unknown> | undefined;
    if (overridesText.trim()) {
      try {
        overrides = JSON.parse(overridesText) as Record<string, unknown>;
      } catch {
        return;
      }
    }
    install.mutate(
      {
        // B-2: field name is template_code (not code) per backend schema
        template_code: code,
        ...(version ? { version: Number(version) } : {}),
        ...(overrides !== undefined ? { overrides } : {}),
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      },
    );
  }

  return (
    <div style={s.overlay}>
      <dialog open style={s.dialog} aria-label="Install template">
        <h2 style={s.title}>Install Template</h2>
        <div style={s.group}>
          <label style={s.label} htmlFor="install-code">
            Template code
          </label>
          <input
            id="install-code"
            style={s.input}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. sales_by_family_month"
            aria-label="Template code"
          />
        </div>
        <div style={s.group}>
          <label style={s.label} htmlFor="install-version">
            Version (optional)
          </label>
          <input
            id="install-version"
            style={s.input}
            type="number"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="Latest if empty"
            aria-label="Template version"
          />
        </div>
        <div style={s.group}>
          <label style={s.label} htmlFor="install-overrides">
            Overrides JSON (optional)
          </label>
          <textarea
            id="install-overrides"
            style={s.textarea}
            value={overridesText}
            onChange={(e) => setOverridesText(e.target.value)}
            placeholder='{"title": {"es": "Custom title"}}'
            aria-label="Overrides JSON"
          />
        </div>
        {install.isError && (
          <p style={{ color: tokens.colors.error, fontSize: '0.875rem' }} role="alert">
            Error installing template.
          </p>
        )}
        <div style={s.actions}>
          <button type="button" style={s.btnCancel} onClick={onClose} aria-label="Cancel">
            Cancel
          </button>
          <button
            type="button"
            style={{ ...s.btnPrimary, opacity: install.isPending || !code ? 0.7 : 1 }}
            onClick={handleInstall}
            disabled={install.isPending || !code}
            aria-label="Install"
          >
            {install.isPending ? 'Installing…' : 'Install'}
          </button>
        </div>
      </dialog>
    </div>
  );
}
